import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Play } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { triggerDownload } from '../utils/export';
import { SegmentedToggle } from './controls/SegmentedToggle';
import { TextInput } from './controls/TextInput';
import { Alert } from './Alert';
import Papa from 'papaparse';
import './BatchView.css';

export const BatchView: React.FC = () => {
  const { t } = useTranslation('batch');
  const config = useAppStore(state => state.config);
  const logoFile = useAppStore(state => state.logoFile);

  const [csvContent, setCsvContent] = useState<string>('');
  const [hasHeader, setHasHeader] = useState(true);
  const [exportFormat, setExportFormat] = useState<'png' | 'svg'>('png');
  const [filenameTemplate, setFilenameTemplate] = useState('qr-{index}');
  
  const downloadSampleCSV = () => {
    const content = "payload,filename\nhttps://example.com,example-1\nhttps://google.com,example-2";
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'MamaQR-sample.csv';
    link.click();
  };
  
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const workerRef = useRef<Worker | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setStatus('error');
      setError(t('batch.error.size', 'CSV file is too large (max 2MB).'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setCsvContent(text);
      
      const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
      const numRows = parsed.data.length;
      setRowCount(numRows);
      
      if (numRows === 0) {
        setStatus('error');
        setError(t('batch.error.empty', 'CSV is empty.'));
      } else if (numRows > 500) {
        setStatus('error');
        setError(t('batch.error.limit', 'Batch limit exceeded (maximum 500 rows).'));
      } else {
        setStatus('idle');
        setError(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const startBatch = () => {
    if (!csvContent) return;
    setStatus('processing');
    setProgress(0);
    setError(null);

    workerRef.current = new Worker(new URL('../workers/batchWorker.ts', import.meta.url), {
      type: 'module'
    });

    workerRef.current.onmessage = (e: MessageEvent<{ type: string; progress: number; blob: Blob; error: string }>) => {
      if (e.data.type === 'progress') {
        setProgress(e.data.progress);
      } else if (e.data.type === 'success') {
        setStatus('success');
        triggerDownload(e.data.blob, `MamaQR-batch-${new Date().getTime()}.zip`);
        workerRef.current?.terminate();
      } else if (e.data.type === 'error') {
        setStatus('error');
        setError(e.data.error);
        workerRef.current?.terminate();
      }
    };

    workerRef.current.onerror = (err) => {
      setStatus('error');
      setError(err.message || 'Worker thread error');
      workerRef.current?.terminate();
    };

    workerRef.current.postMessage({
      csvContent,
      config,
      logoFile,
      hasHeader,
      exportFormat,
      filenameTemplate
    });
  };

  return (
    <div className="batch-view">
      <div className="batch-view-content">
        <h2 className="batch-title">{t('batch.title', 'Batch Processing')}</h2>
        <p className="batch-description">
          {t('batch.description', 'Generate up to 500 QR codes at once from a CSV file. The first column is encoded as the QR payload. Other columns can be used for filenames.')}
          <button className="link-btn link-inline" onClick={downloadSampleCSV}>
            {t('batch.downloadSample', 'Download sample CSV')}
          </button>
        </p>

        <div className="batch-section">
          <h3>1. {t('batch.step1', 'Upload CSV File')}</h3>
          <button type="button" className="csv-upload-area" onClick={() => fileInputRef.current?.click()}>
            <Upload size={24} />
            <span>{csvContent ? t('batch.csvLoaded', `Loaded ${rowCount} row(s)`) : t('batch.csvPlaceholder', 'Click to upload CSV')}</span>
            <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileUpload} className="hidden" tabIndex={-1} />
          </button>
        </div>

        <div className="batch-section">
          <h3>2. {t('batch.step2', 'Configure Options')}</h3>
          <div className="batch-options">
            <div className="checkbox-group">
              <input 
                type="checkbox" 
                id="has-header" 
                checked={hasHeader} 
                onChange={(e) => { 
                  setHasHeader(e.target.checked); 
                  if (status === 'error' && rowCount > 0 && rowCount <= 500) {
                    setStatus('idle');
                    setError(null);
                  }
                }} 
              />
              <label htmlFor="has-header">{t('batch.hasHeader', 'First row is header')}</label>
            </div>
            
            <div className="input-group">
              <SegmentedToggle
                label={t('batch.format', 'Export Format')}
                options={[
                  { value: 'png', label: 'PNG' },
                  { value: 'svg', label: 'SVG' }
                ]}
                value={exportFormat}
                onChange={(v) => { 
                  setExportFormat(v); 
                  if (status === 'error' && rowCount > 0 && rowCount <= 500) {
                    setStatus('idle');
                    setError(null);
                  }
                }}
              />
            </div>

            <TextInput
              label={t('batch.filename', 'Filename Template')}
              type="text"
              value={filenameTemplate}
              onChange={(e) => { 
                setFilenameTemplate(e.target.value); 
                if (status === 'error' && rowCount > 0 && rowCount <= 500) {
                  setStatus('idle');
                  setError(null);
                }
              }}
              placeholder="qr-{index}"
              hint={t('batch.filenameHelp', 'Use {index} for row number, {col1}, {col2} for columns. If "First row is header" is checked, use {header_name}.')}
            />
          </div>
        </div>

        <div className="batch-section">
          <h3>3. {t('batch.step3', 'Generate')}</h3>
          <p className="help-text mb-sm">
            {t('batch.designNote', 'Batch mode inherits design settings (colors, shapes, logo) from Create mode. Frames are not supported in batch generation.')}
          </p>
          <button 
            className="btn-primary start-batch-btn" 
            onClick={startBatch}
            disabled={!csvContent || status === 'error' || status === 'processing'}
          >
            <Play size={18} className="rtl-flip" />
            {t('batch.start', 'Start Batch Processing')}
          </button>
        </div>

        {status === 'processing' && (
          <div className="batch-progress-area">
            <div 
              className="progress-bar-container" 
              role="progressbar" 
              aria-valuenow={progress} 
              aria-valuemin={0} 
              aria-valuemax={100}
            >
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <span>{progress}% {t('batch.processing', 'Processing...')}</span>
          </div>
        )}

        {status === 'success' && (
          <Alert type="success" message={t('batch.success', 'Batch processing complete! Your ZIP file should download automatically.')} />
        )}

        {status === 'error' && (
          <Alert type="error" message={error} />
        )}
      </div>
    </div>
  );
};
